import { useCallback } from 'react'

import { companies } from '../../lib/api/resources.ts'
import type { CompanyResponse, PagedResponse } from '../../lib/api/types.ts'
import { useResource } from '../../lib/useResource.ts'
import type { Resource } from '../../lib/useResource.ts'
import { SelectField } from '../primitives/Field.tsx'

/**
 * O cadastro de empresas (`GET /api/v1/companies`), para seletores da gestão da
 * Black Ghost. Uma página de 200 — o teto da API — basta para um seletor; acima
 * disso o seletor precisaria de busca, e o resumo da entrega registra o limite.
 *
 * Só a Black Ghost usa isto: a experiência de cliente nunca escolhe empresa.
 */
export function useCompanyList(): Resource<PagedResponse<CompanyResponse>> {
  const fetcher = useCallback(
    (signal: AbortSignal) => companies.list({ page: 1, pageSize: 200 }, signal),
    [],
  )
  return useResource(fetcher)
}

/** Nome de exibição de uma empresa pelo id, com fallback curto enquanto carrega. */
export function companyNameOf(
  list: PagedResponse<CompanyResponse> | null,
  id: string | null,
): string | null {
  if (id === null) return null
  return list?.items.find((company) => company.id === id)?.name ?? `empresa ${id.slice(0, 8)}`
}

/**
 * Seletor de empresa de um formulário ou filtro. `allLabel` acrescenta a opção
 * vazia ("todas") para filtros; num formulário obrigatório, omita-o.
 */
export function CompanySelect({
  list,
  value,
  onChange,
  label = 'Empresa',
  allLabel,
  placeholder,
  error,
  hint,
  className = 'w-56',
}: {
  list: Resource<PagedResponse<CompanyResponse>>
  value: string
  onChange: (companyId: string) => void
  label?: string
  allLabel?: string
  /** Opção vazia de um campo obrigatório ("escolha a empresa"). */
  placeholder?: string
  error?: string | null
  hint?: string
  className?: string
}) {
  const head =
    allLabel !== undefined
      ? [{ value: '', label: allLabel }]
      : placeholder !== undefined
        ? [{ value: '', label: placeholder }]
        : []

  const options = [
    ...head,
    ...(list.data?.items ?? [])
      .filter((company) => company.deletedAt === null)
      .map((company) => ({
        value: company.id,
        label: company.isActive
          ? `${company.companyCode} — ${company.name}`
          : `${company.companyCode} — ${company.name} (desativada)`,
      })),
  ]

  return (
    <SelectField
      label={label}
      value={value}
      onValueChange={onChange}
      options={options}
      error={error ?? null}
      hint={list.error !== null ? 'Não foi possível carregar as empresas.' : hint}
      disabled={list.data === null}
      className={className}
    />
  )
}
